import React, { useMemo } from 'react';
import { Radar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    TooltipItem,
} from 'chart.js';
import { RehabilitationData } from '../../../services/network/form30SectorCharts-api';
import styles from './RehabilitationChart.module.css';

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

interface RehabilitationChartProps {
    data: RehabilitationData | null;
    sectorName?: string;
}

export const RehabilitationChart: React.FC<RehabilitationChartProps> = React.memo(({ data, sectorName }) => {
    // Проверяем, есть ли хоть какие-то данные
    const hasData = useMemo(() => {
        if (!data) return false;
        return (
            data.treatment_ambulatory !== null ||
            data.treatment_stationary !== null ||
            data.rehabilitation_ambulatory !== null ||
            data.rehabilitation_remote !== null ||
            data.sanatorium !== null
        );
    }, [data]);

    // Вычисляем максимальное значение для правильного масштабирования
    const maxValue = useMemo(() => {
        if (!data) return 100;
        const values = [
            data.treatment_ambulatory || 0,
            data.treatment_stationary || 0,
            data.rehabilitation_ambulatory || 0,
            data.rehabilitation_remote || 0,
            data.sanatorium || 0,
        ];
        const max = Math.max(...values);
        
        // Округляем до ближайшего "красивого" числа
        if (max <= 100) return 100;
        if (max <= 200) return 200;
        if (max <= 500) return Math.ceil(max / 100) * 100; // Округляем до сотен
        if (max <= 1000) return Math.ceil(max / 200) * 200; // Округляем до 200
        if (max <= 5000) return Math.ceil(max / 500) * 500; // Округляем до 500
        if (max <= 10000) return Math.ceil(max / 1000) * 1000; // Округляем до тысяч
        return Math.ceil(max / 5000) * 5000; // Округляем до 5000 для очень больших значений
    }, [data]);

    const chartData = {
        labels: [
            ['Лечение', 'АПУ'],
            ['Лечение в', 'стационаре'],
            ['Реабилитация', 'в АПУ'],
            ['Реабилитация в', 'стационаре'],
            ['Сан-кур', 'лечение']
        ],
        datasets: [
            {
                label: 'Количество детей',
                data: data ? [
                    data.treatment_ambulatory || 0,
                    data.treatment_stationary || 0,
                    data.rehabilitation_ambulatory || 0,
                    data.rehabilitation_remote || 0,
                    data.sanatorium || 0,
                ] : [0, 0, 0, 0, 0],
                backgroundColor: '#1A64A5BD',
                borderColor: '#1A64A5',
                borderWidth: 2,
                pointBackgroundColor: '#1A64A5',
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: '#1A64A5',
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
            r: {
                beginAtZero: true,
                max: maxValue, // Явно устанавливаем максимум
                ticks: {
                    display: true,
                    stepSize: maxValue / 5, // Делим на 5 равных частей
                },
                pointLabels: {
                    font: {
                        size: 11,
                        weight: 'bold' as const,
                    },
                    padding: 5,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function(context: TooltipItem<'radar'>) {
                        const labels = [
                            'Лечение АПУ',
                            'Лечение в стационаре',
                            'Реабилитация в АПУ',
                            'Реабилитация в стационаре',
                            'Сан-кур лечение'
                        ];
                        const label = labels[context.dataIndex] || '';
                        const value = context.parsed.r || 0;
                        return `${label}: ${value}`;
                    }
                }
            }
        },
    };

    return (
        <div className={styles.chartContainer}>
            <h3 className={styles.chartTitle}>
                Рекомендовано лечение и реабилитация
                {sectorName && <span className={styles.sectorName}> - {sectorName}</span>}
            </h3>
            <div className={styles.chartWrapper}>
                {hasData ? (
                    <Radar data={chartData} options={options} />
                ) : (
                    <div className={styles.noData}>
                        <p>Нет данных для построения графика</p>
                    </div>
                )}
            </div>
        </div>
    );
});
